import React, { useContext, useEffect } from "react";
import getVariantColor from "helpers/get-variant-color";
import getVariantColorImage from "helpers/get-variant-color-image";
import { PatioContext } from "context/patio-calculator";
import extractGid from "helpers/extract-gid";
import getMetafield from "helpers/get-product-metafields";
import getAdjustedVariants from "helpers/patio-sort";
import Measurement from "./measurement";
import Personalization from "./personalization";
import Customization from "./customization";
import patioData from "./data";
import RadioButton from "../../_components/radio-button";
import ColorSwatch from "../../_components/color-swatch";
import FileUpload from "../../_components/file-upload";
import InputField from "../../_components/input-field";

function hasPrintSides(customization) {
    return customization?.variants?.length && customization?.variants?.every((variant) => variant.image);
}

const PatioConfigurator = ({ product, onVariantChange, quantity, variantColors }) => {
    const fabric = product?.currentVariant?.node?.selectedOptions[0].value;
    const color = product?.currentVariant?.node?.selectedOptions[1].value;
    const { currentFabricVariants, firstFabricVariants } = getAdjustedVariants(product, fabric);
    const measureFile = getMetafield(product, "measuring_instructions", "exactMatch")?.node?.value;
    const { updateConfiguration, customAttributes, setCustomAttributes } = useContext(PatioContext);
    const { variantUSP, variantText } = patioData;
    const features = Object.entries(variantText).find((item) => item[0] === fabric.toLowerCase())[1];
    const dynamicCustomizations = product?.customizations;

    useEffect(() => {
        updateConfiguration({ variant: +extractGid(product?.currentVariant?.node?.id), amount: quantity });
    }, [quantity, product?.currentVariant]);

    let currentIndex = 3;

    // get referenceImages from customAttributes as array
    const defaultReferenceImages = Object.keys(customAttributes)
        .filter((key) => key.startsWith("referenceImage"))
        .map((key) => customAttributes[key]);

    return (
        <div className="w-full text-blue-700">
            <Measurement
                file={measureFile}
                measurements={product?.measurements}
                updateConfiguration={updateConfiguration}
            />

            <div className="configurator-section">
                <div className="configurator-section__header">
                    <h3 className="configurator-section__header-title">2. Select fabric</h3>
                    <span className="configurator-section__header-link" />
                </div>

                <div className="configurator-section__inner">
                    <div className="configurator-section__inner-fabrics">
                        {/* ToDo: Change this to be like the colors below */}
                        {firstFabricVariants.map((variant) => (
                            <div className="configurator-section__inner-fabrics-options">
                                <RadioButton
                                    options={[{
                                        label: variant?.node?.title.split(" / ")[0],
                                        value: variant.node.selectedOptions[0].value,
                                    }]}
                                    value={product?.currentVariant?.node?.selectedOptions[0].value}
                                    checked={product?.currentVariant?.node?.selectedOptions[0].value === variant.node.selectedOptions[0].value}
                                    onChange={(value) => onVariantChange({ name: variant.node.selectedOptions[0].name, value })}
                                />
                            </div>
                        ))}
                    </div>

                    <div className="configurator-section__inner-color-swatch">
                        <ColorSwatch
                            colors={currentFabricVariants.map((variant) => ({
                                identifier: variant.node.id,
                                name: variant.node.selectedOptions[1].value,
                                color: getVariantColor(variantColors, variant.node.title.split(" / ")[1].replace(/[/\s]/g, "-").toLowerCase(), variant.node.title.split(" / ")[0].replace(/[/\s]/g, "-"), "patio"),
                                image: getVariantColorImage(variantColors, variant.node.title.split(" / ")[1].replace(/[/\s]/g, "-").toLowerCase(), variant.node.title.split(" / ")[0].replace(/[/\s]/g, "-"), "patio"),
                            }))}
                            value={color}
                            onChange={(colorName) => {
                                const selectedVariant = currentFabricVariants.find((variant) => variant.node.selectedOptions[1].value === colorName);
                                if (selectedVariant) {
                                    onVariantChange({
                                        name: selectedVariant.node.selectedOptions[1].name,
                                        value: selectedVariant.node.selectedOptions[1].value,
                                    });
                                }
                            }}
                        />
                    </div>

                    {variantText && (
                        <div className="configurator-section__inner-variant-features">
                            {features.map((feature) => (
                                <div className="configurator-section__inner-variant-features-items" key={feature?.key}>
                                    <div className="configurator-section__inner-variant-features-items-label">
                                        {feature.key}
                                        :
                                    </div>
                                    <div className="configurator-section__inner-variant-features-items-value">{feature.value}</div>
                                </div>
                            ))}
                        </div>
                    )}

                    {variantUSP && (
                        <div className="configurator-section__inner-variant-usp">
                            {variantUSP.map((usp) => (usp?.text[fabric.toLowerCase()] && (
                                <div className="configurator-section__inner-variant-usp-details" key={usp?.key}>
                                    <div style={{ backgroundImage: `url(${usp?.image})` }} className="configurator-section__inner-variant-usp-details-features" />
                                    <div className="configurator-section__inner-variant-usp-details-features-title">{usp?.key}</div>
                                    <div className="configurator-section__inner-variant-usp-details-features-subtext" dangerouslySetInnerHTML={{ __html: usp?.text[fabric.toLowerCase()] }} />
                                </div>
                            )))}
                        </div>
                    )}
                </div>
            </div>

            {dynamicCustomizations?.map((customization) => {
                if (!hasPrintSides(customization)) return null;

                if (customization.type === "Grid" || customization.type === "Dropdown") {
                    return (
                        <Customization
                            key={customization.id}
                            customizations={[customization]}
                            startIndex={currentIndex++}
                        />
                    );
                }
                if (customization.type === "Personalize") {
                    return (
                        <Personalization
                            key={customization.id}
                            data={customization}
                            bgColor={getVariantColor(
                                variantColors,
                                color.replace(/[/\s]/g, "-").toLowerCase(),
                                fabric.replace(/[/\s]/g, "-").toLowerCase(),
                                "patio",
                            )}
                            index={currentIndex++}
                        />
                    );
                }

                return null;
            })}

            <div className="configurator-section">
                <div className="configurator-section__header">
                    <h3 className="configurator-section__header-title">{`${currentIndex}. Upload Reference Image`}</h3>
                    <span className="configurator-section__header-link">Optional</span>
                </div>
                <div className="configurator-section__inner">
                    <FileUpload
                        multiple
                        formats={["image/jpeg", "image/png", "application/pdf", "image/gif"]}
                        mbLimit={20}
                        defaultValue={defaultReferenceImages}
                        onChange={(files) => {
                            const updatedAttributes = { ...customAttributes };

                            Object.keys(updatedAttributes).forEach((key) => {
                                if (key.includes("referenceImage")) {
                                    delete updatedAttributes[key];
                                }
                            });

                            files.forEach((file, index) => {
                                updatedAttributes[`referenceImage${index === 0 ? "" : index + 1}`] = file;
                            });

                            setCustomAttributes(updatedAttributes);
                        }}
                    />

                    <p className="configurator-section__inner-upload-text">
                        Got a unique shape to cover & want a great fit? Help us with an image, and we will make sure it fits.
                    </p>

                    <h4 className="text-sm mb-2">Any special request for us?</h4>

                    <InputField
                        type="text"
                        value={customAttributes?.specialRequest}
                        onChange={(event) => setCustomAttributes({ ...customAttributes, specialRequest: event.target.value })}
                        placeholder="Enter Product or Brand name to help us serve you better."
                        textarea
                    />
                </div>
            </div>
        </div>
    );
};

export default PatioConfigurator;
